import React, { useEffect, useState } from "react";
import {
  Card, Button, Input, Select, InputNumber, Modal, Tag, Popconfirm,
  notification, Typography, Collapse, Tooltip,
} from "antd";
import {
  PlusOutlined, DeleteOutlined, EditOutlined, CheckOutlined, CloseOutlined,
  ThunderboltOutlined, InfoCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { baseUrl } from "../../../config";

const { Option } = Select;
const { Text } = Typography;
const { Panel } = Collapse;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
});

const CONDITION_FIELDS = [
  { value: "amount", label: "Amount (₹)" },
];

export default function RulesEngine({ posters }) {
  const [triggers, setTriggers]         = useState([]);
  const [addTriggerModal, setAddTriggerModal] = useState(false);
  const [newTKey, setNewTKey]           = useState("");
  const [newTName, setNewTName]         = useState("");
  const [newTDesc, setNewTDesc]         = useState("");
  const [creatingT, setCreatingT]       = useState(false);
  const [ruleModal, setRuleModal]       = useState(null); // { trigger_key, rule? }
  const [ruleForm, setRuleForm]         = useState({});
  const [savingRule, setSavingRule]     = useState(false);

  useEffect(() => { fetchTriggers(); }, []);

  const fetchTriggers = async () => {
    try {
      const res = await axios.get(`${baseUrl}/poster-config/triggers`, { headers: authHeaders() });
      if (res.data.status) setTriggers(res.data.result || []);
    } catch (e) {
      notification.error({ message: "Failed to load triggers.", placement: "topRight" });
    }
  };

  // ── Trigger CRUD ────────────────────────────────────────────────────────────
  const handleCreateTrigger = async () => {
    if (!newTKey.trim() || !newTName.trim()) {
      notification.warning({ message: "Key and Name are required.", placement: "topRight" });
      return;
    }
    setCreatingT(true);
    try {
      const res = await axios.post(
        `${baseUrl}/poster-config/triggers/create`,
        { trigger_key: newTKey.trim(), trigger_name: newTName.trim(), description: newTDesc.trim() },
        { headers: authHeaders() }
      );
      if (res.data.status) {
        notification.success({ message: res.data.message, placement: "topRight" });
        setAddTriggerModal(false);
        setNewTKey(""); setNewTName(""); setNewTDesc("");
        fetchTriggers();
      } else {
        notification.error({ message: res.data.message, placement: "topRight" });
      }
    } catch (e) {
      notification.error({ message: e.message, placement: "topRight" });
    } finally {
      setCreatingT(false);
    }
  };

  const handleDeleteTrigger = async (trigger_key) => {
    try {
      const res = await axios.delete(`${baseUrl}/poster-config/triggers/${trigger_key}`, { headers: authHeaders() });
      if (res.data.status) {
        notification.success({ message: res.data.message, placement: "topRight" });
        fetchTriggers();
      } else {
        notification.error({ message: res.data.message, placement: "topRight" });
      }
    } catch (e) {
      notification.error({ message: e.message, placement: "topRight" });
    }
  };

  // ── Rule CRUD ───────────────────────────────────────────────────────────────
  const openAddRule = (trigger_key) => {
    setRuleForm({ trigger_key, condition_field: "amount", rule_order: 0 });
    setRuleModal({ trigger_key });
  };

  const openEditRule = (trigger_key, rule) => {
    setRuleForm({ ...rule, trigger_key });
    setRuleModal({ trigger_key, rule });
  };

  const handleSaveRule = async () => {
    if (!ruleForm.poster_key) {
      notification.warning({ message: "Select a poster.", placement: "topRight" });
      return;
    }
    setSavingRule(true);
    try {
      let res;
      if (ruleModal?.rule?.id) {
        res = await axios.patch(
          `${baseUrl}/poster-config/rules/${ruleModal.rule.id}`,
          ruleForm,
          { headers: authHeaders() }
        );
      } else {
        res = await axios.post(
          `${baseUrl}/poster-config/rules/create`,
          ruleForm,
          { headers: authHeaders() }
        );
      }
      if (res.data.status) {
        notification.success({ message: res.data.message, placement: "topRight" });
        setRuleModal(null);
        fetchTriggers();
      } else {
        notification.error({ message: res.data.message, placement: "topRight" });
      }
    } catch (e) {
      notification.error({ message: e.message, placement: "topRight" });
    } finally {
      setSavingRule(false);
    }
  };

  const handleDeleteRule = async (id) => {
    try {
      const res = await axios.delete(`${baseUrl}/poster-config/rules/${id}`, { headers: authHeaders() });
      if (res.data.status) {
        notification.success({ message: res.data.message, placement: "topRight" });
        fetchTriggers();
      } else {
        notification.error({ message: res.data.message, placement: "topRight" });
      }
    } catch (e) {
      notification.error({ message: e.message, placement: "topRight" });
    }
  };

  const isEditingRule = !!ruleModal?.rule?.id;

  return (
    <Card
      bordered={false}
      title={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>
            <ThunderboltOutlined style={{ marginRight: 6 }} />
            Poster Rules Engine
          </span>
          <Button size="small" icon={<PlusOutlined />} onClick={() => setAddTriggerModal(true)}>
            Add Trigger
          </Button>
        </div>
      }
      headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}
      style={{ backgroundColor: "#fff", borderRadius: 8, marginBottom: 16 }}
      bodyStyle={{ padding: "16px" }}
    >
      <p style={{ color: "#666", fontSize: 12, marginBottom: 16 }}>
        Each <b>Trigger</b> is an operation in the backend (birthday cron, new payment, etc.).
        Each trigger has <b>Rules</b> — conditions that pick which poster to send.
        Rules are checked in order — first match wins.
        <Tooltip title="Backend usage: const { textConfig, posterImage } = await resolveForTrigger('trigger_key', { amount: 1500 })">
          <InfoCircleOutlined style={{ marginLeft: 6, color: "#1890ff", cursor: "pointer" }} />
        </Tooltip>
      </p>

      <Collapse accordion>
        {triggers.map(trigger => (
          <Panel
            key={trigger.trigger_key}
            header={
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Tag color="geekblue" style={{ fontFamily: "monospace", fontSize: 11 }}>{trigger.trigger_key}</Tag>
                <span style={{ fontWeight: 600 }}>{trigger.trigger_name}</span>
                {trigger.description && (
                  <Text type="secondary" style={{ fontSize: 11 }}>— {trigger.description}</Text>
                )}
              </div>
            }
            extra={
              <Popconfirm
                title="Delete trigger and all its rules?"
                onConfirm={e => { e.stopPropagation(); handleDeleteTrigger(trigger.trigger_key); }}
                okText="Yes" cancelText="No"
              >
                <Button
                  size="small" danger icon={<DeleteOutlined />}
                  onClick={e => e.stopPropagation()}
                >
                  Delete
                </Button>
              </Popconfirm>
            }
          >
            {/* Rule table */}
            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 10 }}>
              <thead>
                <tr style={{ background: "#f5f5f5" }}>
                  <th style={th}>Order</th>
                  <th style={th}>Rule / Condition</th>
                  <th style={th}>Poster</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {(trigger.rules || []).map((rule, i) => (
                  <tr key={rule.id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                    <td style={{ ...td, width: 50, textAlign: "center" }}>
                      <Tag>{rule.rule_order}</Tag>
                    </td>
                    <td style={td}>
                      {rule.condition_field ? (
                        <span>
                          <Tag color="orange">{rule.condition_field}</Tag>
                          {rule.condition_min !== null && <span>≥ ₹{Number(rule.condition_min).toLocaleString("en-IN")} </span>}
                          {rule.condition_min !== null && rule.condition_max !== null && <span>and </span>}
                          {rule.condition_max !== null && <span>≤ ₹{Number(rule.condition_max).toLocaleString("en-IN")}</span>}
                        </span>
                      ) : (
                        <Tag color="green">Always (fallback)</Tag>
                      )}
                      {rule.rule_name && <Text type="secondary" style={{ marginLeft: 8, fontSize: 11 }}>{rule.rule_name}</Text>}
                    </td>
                    <td style={td}>
                      {rule.poster_name
                        ? <Tag color="blue">{rule.poster_name}</Tag>
                        : <Tag color="red">Poster not found</Tag>}
                    </td>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>
                      <Button size="small" icon={<EditOutlined />} style={{ marginRight: 4 }}
                        onClick={() => openEditRule(trigger.trigger_key, rule)} />
                      <Popconfirm title="Delete this rule?" onConfirm={() => handleDeleteRule(rule.id)} okText="Yes" cancelText="No">
                        <Button size="small" danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    </td>
                  </tr>
                ))}
                {(!trigger.rules || trigger.rules.length === 0) && (
                  <tr><td colSpan={4} style={{ padding: 12, color: "#999", textAlign: "center" }}>
                    No rules yet — add one below.
                  </td></tr>
                )}
              </tbody>
            </table>
            <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={() => openAddRule(trigger.trigger_key)}>
              Add Rule
            </Button>

            {/* Backend usage hint */}
            <div style={{ marginTop: 10, background: "#f6f8fa", borderRadius: 4, padding: "6px 10px", fontSize: 11, color: "#555" }}>
              <b>Backend usage:</b>
              <code style={{ marginLeft: 6 }}>{`await resolveForTrigger('${trigger.trigger_key}', { amount })`}</code>
            </div>
          </Panel>
        ))}
        {triggers.length === 0 && (
          <div style={{ textAlign: "center", padding: 24, color: "#999" }}>
            No triggers found. Run <code>sn_poster_rules.sql</code> or click "Add Trigger".
          </div>
        )}
      </Collapse>

      {/* Add Trigger Modal */}
      <Modal
        title="Add New Trigger"
        open={addTriggerModal}
        onOk={handleCreateTrigger}
        onCancel={() => { setAddTriggerModal(false); setNewTKey(""); setNewTName(""); setNewTDesc(""); }}
        confirmLoading={creatingT}
        okText="Create" cancelText="Cancel"
      >
        <div style={{ marginBottom: 10 }}>
          <Text strong>Trigger Key</Text>
          <Text type="secondary" style={{ fontSize: 11, display: "block" }}>
            Used in backend: <code>resolveForTrigger('key', context)</code>
          </Text>
          <Input
            value={newTKey}
            onChange={e => setNewTKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))}
            placeholder="e.g. annual_camp"
            style={{ marginTop: 4 }}
          />
        </div>
        <div style={{ marginBottom: 10 }}>
          <Text strong>Display Name</Text>
          <Input value={newTName} onChange={e => setNewTName(e.target.value)}
            placeholder="e.g. Annual Camp Message" style={{ marginTop: 4 }} />
        </div>
        <div>
          <Text strong>Description <Text type="secondary">(optional)</Text></Text>
          <Input value={newTDesc} onChange={e => setNewTDesc(e.target.value)}
            placeholder="e.g. Sent when camp registration opens" style={{ marginTop: 4 }} />
        </div>
      </Modal>

      {/* Add / Edit Rule Modal */}
      <Modal
        title={isEditingRule ? "Edit Rule" : `Add Rule — ${ruleModal?.trigger_key}`}
        open={!!ruleModal}
        onOk={handleSaveRule}
        onCancel={() => setRuleModal(null)}
        confirmLoading={savingRule}
        okText={isEditingRule ? "Update" : "Add"} cancelText="Cancel"
      >
        <div style={{ marginBottom: 10 }}>
          <Text strong>Rule Label <Text type="secondary">(optional)</Text></Text>
          <Input value={ruleForm.rule_name || ""} onChange={e => setRuleForm(f => ({ ...f, rule_name: e.target.value }))}
            placeholder="e.g. ₹0 – ₹1000" style={{ marginTop: 4 }} />
        </div>

        <div style={{ marginBottom: 10 }}>
          <Text strong>Condition</Text>
          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            <Select
              value={ruleForm.condition_field || "none"}
              onChange={v => setRuleForm(f => ({ ...f, condition_field: v === "none" ? null : v, condition_min: null, condition_max: null }))}
              style={{ width: 140 }}
            >
              <Option value="none">Always match</Option>
              {CONDITION_FIELDS.map(f => <Option key={f.value} value={f.value}>{f.label}</Option>)}
            </Select>
            {ruleForm.condition_field && (
              <>
                <InputNumber
                  placeholder="Min (₹)"
                  value={ruleForm.condition_min ?? undefined}
                  onChange={v => setRuleForm(f => ({ ...f, condition_min: v }))}
                  min={0} style={{ width: 110 }}
                />
                <Text style={{ lineHeight: "32px" }}>to</Text>
                <InputNumber
                  placeholder="Max (₹)"
                  value={ruleForm.condition_max ?? undefined}
                  onChange={v => setRuleForm(f => ({ ...f, condition_max: v }))}
                  min={0} style={{ width: 110 }}
                />
              </>
            )}
          </div>
          {!ruleForm.condition_field && (
            <Text type="secondary" style={{ fontSize: 11 }}>This rule always matches — use as a fallback (put last).</Text>
          )}
        </div>

        <div style={{ marginBottom: 10 }}>
          <Text strong>Poster to Send</Text>
          <Select
            value={ruleForm.poster_key || undefined}
            onChange={v => setRuleForm(f => ({ ...f, poster_key: v }))}
            placeholder="Select poster..."
            style={{ width: "100%", marginTop: 4 }}
          >
            {(posters || []).map(p => <Option key={p.poster_key} value={p.poster_key}>{p.poster_name}</Option>)}
          </Select>
        </div>

        <div>
          <Text strong>Rule Order <Text type="secondary">(lower = checked first)</Text></Text>
          <InputNumber
            value={ruleForm.rule_order ?? 0}
            onChange={v => setRuleForm(f => ({ ...f, rule_order: v }))}
            min={0} style={{ width: "100%", marginTop: 4 }}
          />
        </div>
      </Modal>
    </Card>
  );
}

const th = { padding: "6px 10px", textAlign: "left", fontWeight: 600, fontSize: 12, borderBottom: "2px solid #e8e8e8" };
const td = { padding: "8px 10px", fontSize: 13, verticalAlign: "middle" };
